import {
  blur as nativeBlur,
  focus as nativeFocus,
  onChangeText,
  onKeyPress as nativeOnKeyPress,
  preserveFocus as nativePreserveFocus,
} from "@-/dev.improve.haniverse.modules.persistent-keyboard";
import { useSyncExternalStore } from "react";
import { Keyboard, Platform } from "@-/com.npmjs.react-native";
import {
  AndroidSoftInputModes,
  KeyboardController,
} from "@-/com.npmjs.react-native-keyboard-controller";
import { setEagerKeyboardVisible, useEagerKeyboardVisible } from "#eager-keyboard-visible";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Focus preservation is best-effort, and it is awaited while a rating holds the shared rating
 * latch. The native call only re-asserts first responder on the UI thread (a frame or two), so a
 * call that has not answered within this has been lost (a torn-down view, a busy UI thread): give
 * up on the hand-off rather than parking the rating — and every rating behind the latch — on it.
 */
const PRESERVE_FOCUS_DEADLINE_MS = 1500;

export type PersistentKeyboardEventSubscription = { remove(): void };

let keyboardActive = false;
const listeners = new Set<() => void>();

/**
 * One-shot hand-off token for the drill card-to-card transition.
 *
 * `keyboardActive` only says a session exists somewhere in the app — it stays true across a
 * navigation into the review flow from a screen that happened to have the keyboard up, which made
 * the first drill card summon the keyboard on its own. The reveal / rating path explicitly hands
 * focus forward via `persistentKeyboardPreserveFocus()`, so arm the token there and let the next
 * drill input consume it exactly once. A fresh entry into reviews never arms it, so its answer
 * input starts unfocused until the user taps it.
 */
let focusHandoffArmed = false;

export function armPersistentKeyboardFocusHandoff() {
  focusHandoffArmed = true;
}

/** Returns whether a hand-off was armed, clearing it so it can only be honoured once. */
export function consumePersistentKeyboardFocusHandoff() {
  const armed = focusHandoffArmed;
  focusHandoffArmed = false;
  return armed;
}

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return keyboardActive;
}

function getServerSnapshot() {
  return false;
}

/**
 * Subscribe component visibility to whether the persistent keyboard is currently
 * on screen. Flips synchronously when we call `persistentKeyboardFocus()` /
 * `persistentKeyboardDismiss()`, and is reconciled by the OS `keyboardWill*` /
 * `keyboardDid*` events so it stays correct even if the keyboard is shown/hidden
 * by something other than this module.
 */
export function usePersistentKeyboardVisible() {
  return useEagerKeyboardVisible();
}

let typingWatchInitialized = false;

function initTypingWatch() {
  if (typingWatchInitialized) {
    return;
  }
  typingWatchInitialized = true;
  onChangeText(() => {
    const wasActive = keyboardActive;
    if (!keyboardActive) {
      keyboardActive = true;
      emit();
    }
    if (!wasActive && Platform.OS === "android") {
      KeyboardController.setInputMode(
        AndroidSoftInputModes.SOFT_INPUT_STATE_ALWAYS_VISIBLE |
          AndroidSoftInputModes.SOFT_INPUT_ADJUST_RESIZE,
      );
    }
  });
}

export function usePersistentKeyboardToolbarEnabled() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function persistentKeyboardDismiss() {
  keyboardActive = false;
  focusHandoffArmed = false;
  emit();
  setEagerKeyboardVisible(false);
  nativeBlur();
  // `nativeBlur()` owns the module-backed drill field. `Keyboard.dismiss()` also covers any
  // ordinary React Native input, because the root dismiss control is intentionally app-wide.
  Keyboard.dismiss();
  if (Platform.OS === "android") {
    KeyboardController.setDefaultMode();
  }
}

export async function persistentKeyboardPreserveFocus() {
  if (!KeyboardController.isVisible() && KeyboardController.state().height === 0) {
    return;
  }
  focusHandoffArmed = true;
  try {
    await withDeadline(
      { deadline: PRESERVE_FOCUS_DEADLINE_MS, operation: "persistent keyboard preserveFocus" },
      () => nativePreserveFocus(),
    );
  } catch {
    // Best-effort: the next input simply starts unfocused. The expiry itself is reported.
    return;
  }
  if (!keyboardActive) {
    keyboardActive = true;
    emit();
  }
}

export function persistentKeyboardKeepFocused() {
  // Only refocus when the iOS keyboard is actually on screen — `keyboardActive`
  // also flips true when the shuangpin grid writes via setText, which would
  // otherwise spuriously summon the keyboard on button presses.
  if (KeyboardController.isVisible()) {
    nativeFocus();
  }
}

export function persistentKeyboardFocus() {
  keyboardActive = true;
  emit();
  setEagerKeyboardVisible(true);
  nativeFocus();
}

export function persistentKeyboardOnKeyPress(
  listener: (event: { key: string }) => void,
): PersistentKeyboardEventSubscription {
  return nativeOnKeyPress(listener);
}

export function persistentKeyboardOnDone(
  listener: () => void,
): PersistentKeyboardEventSubscription {
  return nativeOnKeyPress((event) => {
    if (event.key === "Enter") {
      listener();
    }
  });
}

// eslint-disable-next-line no-top/no-top-level-side-effects -- register native onChangeText listener once at module load
initTypingWatch();
